import React, { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import World from '../shared/world';
import Icon from '../shared/icon';
import DAM from '../dam';
import Inspector from '../inspector';

import { initializeFirebase } from '../shared/firebase';
import './home.css';

initializeFirebase();
const firebaseAuthProvider = new firebase.auth.GoogleAuthProvider();

const TAB_WORLDS = 'worlds';
const TAB_ASSETS = 'assets';
const TAB_INSPECTOR = 'inspector';
const TAB_SEQUENCER = 'sequencer';
const TAB_PLAYER = 'preview';

function Header({ label, user, onSignIn, onSignOut }) {
  const [hovering, setHovering] = useState(false);
  return (
    <header className="home__header">
      <span className="home__header-title">{label}</span>
      {user && (
        <div className="home__user" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} onClick={onSignOut}>
          {!hovering && user.email}
          {hovering && 'Sign Out'}
        </div>
      )}
      {!user && (
        <div className="home__user" onClick={onSignIn}>
          Sign In
        </div>
      )}
    </header>
  );
}

function CreateWorldModal({ worlds, onCreate, onCancel }) {
  const [worldName, setWorldName] = useState('');
  const [assetLibraryName, setAssetLibraryName] = useState('');
  const [assetLibraryNames, setAssetLibraryNames] = useState([]);
  useEffect(() => {
    firebase
      .firestore()
      .collection('asset-libraries')
      .get()
      .then((snap) => {
        const assetLibraryNames = [];
        snap.forEach((doc) => assetLibraryNames.push(doc.id));
        setAssetLibraryNames(assetLibraryNames);
        setAssetLibraryName(assetLibraryNames[0]);
      });
  }, []);

  let disabled = false;
  let nameError = '';
  if (worldName.trim().length === 0) {
    nameError = 'Name cannot be empty.';
    disabled = true;
  } else if (!World.isValidName(worldName)) {
    nameError = 'Name should only contain letters and numbers.';
    disabled = true;
  } else if (worlds.find((world) => world.name === worldName)) {
    nameError = `"${worldName}" already exists. Please choose a unique name.`;
    disabled = true;
  }

  return (
    <>
      <div className="modal__backdrop" />
      <div className="modal__pane create-world">
        <div className="form__header">
          <h1>Create new world</h1>
        </div>
        <div className="form__content">
          <div className="form__row">
            <label>World Name</label>
            <input type="text" value={worldName} onChange={(e) => setWorldName(e.target.value)} />
            <div className="form__error">{nameError}</div>
          </div>
          <div className="form__row">
            <label>Asset Library</label>
            <select onChange={(e) => setAssetLibraryName(e.currentTarget.value)} value={assetLibraryName}>
              {assetLibraryNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form__actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="form__primary" disabled={disabled} onClick={() => onCreate(worldName, assetLibraryName)}>
            Create
          </button>
        </div>
      </div>
    </>
  );
}

function WorldCard({ world }) {
  return (
    <a href={`/_tools/editor?world=${world.name}`} className="card" target="_blank" rel="noopener noreferrer">
      {world.name}
    </a>
  );
}

function Worlds() {
  const [worlds, setWorlds] = useState();
  const [createWorldModalVisible, setCreateWorldModalVisible] = useState(false);

  useEffect(() => {
    const db = firebase.firestore();
    db.collection('worlds').onSnapshot((snap) => {
      const worlds = [];
      snap.forEach((doc) => {
        const library = World.fromJSON(JSON.parse(doc.data().json));
        worlds.push(library);
      });
      setWorlds(worlds);
    });
  }, []);

  const onCreateWorld = (worldName, assetLibraryName) => {
    setCreateWorldModalVisible(false);
    const world = World.createEmpty(worldName, 100, 100, assetLibraryName);

    firebase
      .firestore()
      .collection('worlds')
      .doc(worldName)
      .set({ json: JSON.stringify(world.toJSON()) });
  };

  return (
    <>
      <div className="worlds__grid">
        {worlds && worlds.map((world) => <WorldCard key={world.name} world={world} />)}
        <div className="card empty" onClick={() => setCreateWorldModalVisible(true)}>
          <Icon name="plus" size={24} />
          Create
        </div>
      </div>
      {createWorldModalVisible && (
        <CreateWorldModal worlds={worlds} onCreate={onCreateWorld} onCancel={() => setCreateWorldModalVisible(false)} />
      )}
    </>
  );
}

function SequenceCard({ id, title }) {
  return (
    <a href={`/_tools/sequencer/${id}`} className="card" target="_blank" rel="noopener noreferrer">
      {title}
    </a>
  );
}

export default function Home() {
  const [user, setUser] = useState();
  const [activeTab, setActiveTab] = useState(TAB_WORLDS);

  useEffect(() => {
    firebase.auth().onAuthStateChanged(() => {
      setUser(firebase.auth().currentUser);
    });
  }, []);

  const onSignIn = async () => {
    await firebase.auth().signInWithPopup(firebaseAuthProvider);
    setUser(firebase.auth().currentUser);
  };

  const onSignOut = async () => {
    await firebase.auth().signOut();
  };

  return (
    <div className="home">
      <div className="home__sidebar">
        <h1>Parcitypate</h1>
        <div className={`home__tab ${activeTab === TAB_WORLDS ? 'home__tab--active' : ''}`} onClick={() => setActiveTab(TAB_WORLDS)}>
          <span className="home__tab-text">Worlds</span>
        </div>
        <div className={`home__tab ${activeTab === TAB_ASSETS ? 'home__tab--active' : ''}`} onClick={() => setActiveTab(TAB_ASSETS)}>
          <span className="home__tab-text">Assets</span>
        </div>
        <div className={`home__tab ${activeTab === TAB_INSPECTOR ? 'home__tab--active' : ''}`} onClick={() => setActiveTab(TAB_INSPECTOR)}>
          <span className="home__tab-text">GLB Inspector</span>
        </div>
        <div className={`home__tab ${activeTab === TAB_SEQUENCER ? 'home__tab--active' : ''}`} onClick={() => setActiveTab(TAB_SEQUENCER)}>
          <span className="home__tab-text">Sequencer</span>
        </div>
        <div className={`home__tab ${activeTab === TAB_PLAYER ? 'home__tab--active' : ''}`} onClick={() => setActiveTab(TAB_PLAYER)}>
          <span className="home__tab-text">Player</span>
        </div>
      </div>
      <div className="home__main">
        {activeTab === TAB_WORLDS && (
          <>
            <Header label="Worlds" user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
            <Worlds />
          </>
        )}
        {activeTab === TAB_ASSETS && (
          <>
            <Header label="Assets" user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
            <DAM />
          </>
        )}
        {activeTab === TAB_INSPECTOR && (
          <>
            <Header label="GLB Inspector" user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
            <Inspector />
          </>
        )}
        {activeTab === TAB_SEQUENCER && (
          <>
            <Header label="Sequencer" user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
            <div className="worlds__grid">
              <SequenceCard id="edubox" title="EDUbox Slimme Mobiliteit" />
              <SequenceCard id="pajottenland" title="Opgewekt Pajottenland" />
              <SequenceCard id="zaventem" title="Zaventem" />
            </div>
          </>
        )}
        {activeTab === TAB_PLAYER && (
          <>
            <Header label="Player" user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
            <div className="worlds__grid">
              <a className="card" href="/p/edubox" target="_blank">
                EDUbox Slimme Mobiliteit
              </a>
              <a className="card" href="/p/pajottenland" target="_blank">
                Opgewekt Pajottenland
              </a>
              <a className="card" href="/p/zaventem" target="_blank">
                Zaventem
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
